import {
  buildNoteLookup,
  type ManiaChart,
  type ManiaLane,
  type ManiaNote,
} from '../../maniaNote.js';
import { MAX_HP, STARTING_HP, type JudgeVerdict } from '../../gameTypes.js';

/**
 * Why a mania-engine run ended in failure. The universal Results
 * overlay renders copy from this.
 *   - `'drained'`: HP bled to zero over the run (Guitar, and Piano's
 *     forgiving tutorial). Shared with Pulse + Circles.
 *   - `'missed'`: a single missed note ended the run outright. Only
 *     emitted when a mode runs with {@link ManiaEngineConfig.instadeathOnMiss}
 *     (Piano's real play), where there is no HP buffer to drain.
 */
export type ManiaDeathReason = 'drained' | 'missed';

/**
 * One resolvable event the engine tracks. Charts express dual-tile
 * accents as a single {@link ManiaNote} carrying `dualPartnerLane`;
 * we split that into two independent lane events here so the rest of
 * the engine can match taps against a flat lane stream without
 * branching on dual semantics every time.
 *
 * `kind`:
 *   - `primary` = the source note's main lane.
 *   - `dual` = the companion lane. Carries the same beat as its
 *     primary sibling and is judged independently (one tap per
 *     lane); a player who hits the primary but misses the partner
 *     loses HP only for the missed partner. The player isn't
 *     all-or-nothing penalised, but a perfect-only run still
 *     requires both halves of every dual.
 *
 * `holdEndBeat` (optional): turns this event into a hold note. The
 * engine judges the head (tap at `beat`) geometrically — perfect
 * for the full time the tile overlaps the hit bar — then tracks the
 * lane as "held" until either the player releases or the
 * late-release window expires. Release timing is judged with the
 * symmetric ms-window `judge()` against `holdEndBeat` (it's a
 * moment-in-time event, not a visual overlap), so head and release
 * use different windows by design.
 */
export interface ManiaLaneEvent {
  id: string;
  beat: number;
  lane: ManiaLane;
  /**
   * End beat for hold notes. When present the engine treats the event
   * as a hold: head judged at `beat`, release judged at `holdEndBeat`.
   * When absent the event is a single tap (the common case).
   */
  holdEndBeat?: number;
  kind: 'primary' | 'dual';
  /** Source note id this event was expanded from (same for both halves of a dual). */
  sourceId: string;
}

/**
 * Resolution record for one lane event. Hold notes produce TWO
 * resolutions: one with `kind: 'head'` when the player taps the
 * leading edge, and one with `kind: 'release'` when they let go
 * (or when the late-release auto-resolves). Tap-only notes produce
 * just the head resolution.
 *
 * Stored on the engine state so the session / results overlay can
 * replay verdict-by-verdict without re-deriving from raw events.
 */
export interface ManiaResolution {
  laneEventId: string;
  /** The source note id (matches `id` for non-dual notes; same on both halves of a dual). */
  sourceId: string;
  lane: ManiaLane;
  /**
   * Which phase of the lane event this resolution scored:
   *   - `head`: initial tap (single tile or hold's leading edge).
   *   - `release`: end of a hold (player let go, or auto-released
   *     by the late-release window passing).
   */
  kind: 'head' | 'release';
  verdict: JudgeVerdict;
  /** Input-time minus target-time, signed. */
  diffMs: number;
  beat: number;
  /** Combo count immediately before this resolution applied. */
  previousCombo: number;
  /**
   * Score added to the run total by this resolution. 0 for misses.
   * Pre-computed so UI consumers (score-pops, telemetry) don't have
   * to mirror the engine's `baseScore × comboMultiplier` formula —
   * mirroring drifts the moment scoring constants change.
   */
  scoreDelta: number;
}

/**
 * In-flight hold state for one lane. Lives on the engine state from
 * the moment a hold's head is successfully tapped until the lane is
 * released (by the player or by the late-release auto-resolve).
 *
 * Held lanes carry their own scoring: subsequent `down` events in the
 * lane are no-ops (no empty-tap penalty — the player's finger is
 * already on the key), and the only event that releases the hold is
 * an `up` on the same lane.
 */
export interface ActiveHold {
  /** Lane-event id of the head we're holding (matches a ManiaLaneEvent.id). */
  laneEventId: string;
  /** Source note id (same value for both halves of a chord-hold). */
  sourceId: string;
  /** Which lane this hold lives in (also the key in ManiaEngineState.heldLanes). */
  lane: ManiaLane;
  /** Beat the hold should release at. */
  holdEndBeat: number;
}

export interface ManiaEngineState {
  beat: number;
  combo: number;
  maxCombo: number;
  perfects: number;
  goods: number;
  misses: number;
  score: number;
  resolutions: ManiaResolution[];
  alive: boolean;
  completed: boolean;
  deathReason: ManiaDeathReason | null;
  deathBeat: number | null;
  /**
   * Lane-event ids whose head has been resolved. For tap-only notes
   * this is also the terminal state. For hold notes the head id is
   * added here when the head fires, and the corresponding entry in
   * {@link heldLanes} tracks the in-flight body until release.
   */
  resolvedIds: Set<string>;
  /**
   * Hold-tail resolution ids (one entry per hold whose release has
   * fired, by player input or auto-release). Separate from
   * {@link resolvedIds} so the auto-expire logic can tell a missed
   * head ("never tapped") from an active hold whose head was tapped
   * but tail hasn't released yet.
   */
  resolvedReleaseIds: Set<string>;
  /**
   * In-flight holds keyed by lane (at most one active hold per lane —
   * the engine refuses to engage a second hold on a lane until the
   * first releases). Empty when no holds are currently in flight.
   */
  heldLanes: Map<ManiaLane, ActiveHold>;
  hp: number;
  maxHp: number;
  /** Beat of the most recent miss, used to gate passive HP regen. */
  lastMissBeat: number | null;
  /** Consecutive miss count for the streak-decaying damage curve. */
  consecutiveMissCount: number;
  /** Beats accumulated toward the next regen tick. See HP_REGEN_INTERVAL_BEATS. */
  regenProgressBeats: number;
}

export interface ManiaEngineConfig {
  chart: ManiaChart;
  /**
   * Lane events sorted by `(beat asc, id asc)`. The engine reads this
   * — never `chart.notes` directly — so replay determinism does not
   * depend on the JSON producer's array order. Built once via
   * {@link buildLaneEventLookup} when the config is constructed.
   */
  sortedLaneEvents: ReadonlyArray<ManiaLaneEvent>;
  /**
   * Perfect ms window for hold-release judgement (the release tier
   * is symmetric around `holdEndBeat`). Tap heads use the geometric
   * `perfectOverlap*` windows instead.
   */
  perfectWindowMs: number;
  /** Good ms window for hold-release judgement. See {@link perfectWindowMs}. */
  goodWindowMs: number;
  /**
   * Good window in beats. Used by `expireStaleHolds` to scale the
   * late-release grace cutoff (1.5× this).
   */
  goodWindowBeats: number;
  /**
   * Geometric perfect-zone span in milliseconds. A tap on a head
   * resolves to PERFECT for `diffMs in [0, perfectOverlapMs]` — i.e.
   * any time the tile visually overlaps the hit bar, from leading
   * edge first touching the line through trailing edge crossing it.
   * Derived from `chart.approachMs × tuning.perfectOverlapFrac`
   * so the engine and renderer agree on where "perfect" lives.
   */
  perfectOverlapMs: number;
  /** {@link perfectOverlapMs} in beats. */
  perfectOverlapBeats: number;
  /**
   * Early-tap good window for keyboard input, in ms. A tap with
   * `diffMs in [-keyboardGoodEarlyMs, 0)` resolves to GOOD; earlier
   * than that is an empty tap (no resolution, eventual auto-miss).
   * Kept tight so PC players can still genuinely miss by jumping
   * the gun on a tile that's nowhere near the bar yet.
   */
  keyboardGoodEarlyMs: number;
  /** {@link keyboardGoodEarlyMs} in beats. */
  keyboardGoodEarlyBeats: number;
  /**
   * Early-tap good window for pointer (touch / mouse) input, in ms.
   * Wide on purpose: pointer input couples lane choice with vertical
   * tile-targeting in a single gesture, so the player has already
   * "earned" the resolution by physically touching the tile. Any tap
   * on a visible tile in the lane resolves at least as GOOD.
   */
  pointerGoodEarlyMs: number;
  /** {@link pointerGoodEarlyMs} in beats. */
  pointerGoodEarlyBeats: number;
  /**
   * Late-tap good window for keyboard input, in ms. A tap with
   * `diffMs in (perfectOverlapMs, perfectOverlapMs + keyboardGoodLateMs]`
   * resolves to GOOD. Zero when the mode has no late slack.
   */
  keyboardGoodLateMs: number;
  /** {@link keyboardGoodLateMs} in beats. */
  keyboardGoodLateBeats: number;
  /**
   * Late-tap good window for pointer input, in ms. See
   * {@link keyboardGoodLateMs}.
   */
  pointerGoodLateMs: number;
  /** {@link pointerGoodLateMs} in beats. */
  pointerGoodLateBeats: number;
  /**
   * Extra beats after {@link perfectOverlapBeats} before an unresolved
   * head auto-misses. `max(keyboardGoodLateBeats, pointerGoodLateBeats)`.
   */
  autoMissLateBeats: number;
  /**
   * Approach time in beats — how long a tile is visible before its
   * target beat. Derived from `chart.approachMs × beatsPerMs` so the
   * engine and renderer agree on the early-click cutoff.
   */
  approachBeats: number;
  /** Approach time in milliseconds (the BPM-independent reading window). */
  approachMs: number;
  /**
   * Multiplier applied to the per-miss HP damage table
   * ({@link HP_MISS_DAMAGE_BY_STREAK_INDEX}). 1.0 is Piano's default,
   * Guitar uses < 1.0 so the harder-to-read mode doesn't compound
   * "harder timing" with "more HP bleed per miss". Sourced from the
   * mode's {@link ManiaEngineTuning.missDamageMultiplier}.
   */
  missDamageMultiplier: number;
  /**
   * When true, any miss (auto-miss, bad tap, or broken hold) ends the
   * run on the spot rather than chipping HP — there is no buffer and
   * the one-time lifeline does not apply. Piano's real play sets this
   * so a flawless clear is the only win and a slip ends the attempt
   * immediately; Guitar (and Piano's tutorial) leave it false and keep
   * the HP-drain model. Sourced from
   * {@link ManiaEngineTuning.instadeathOnMiss}.
   */
  instadeathOnMiss: boolean;
}

/**
 * Expand a chart's {@link ManiaNote} stream into the flat lane-event
 * stream the engine consumes. Each note produces one entry for its
 * primary lane plus, when present, a second entry for its
 * dual-partner lane. Lane-event ids are derived from the source note
 * id with a `:p` / `:d` suffix so they survive serialisation
 * round-trips and stay deterministic across builds.
 *
 * Sorted by `(beat, id)` — same canonical order the Circles engine
 * uses on its hit objects so client and server agree on tie-break
 * iteration even if the chart JSON is re-serialised.
 */
export function buildLaneEventLookup(notes: ReadonlyArray<ManiaNote>): ManiaLaneEvent[] {
  const sorted = buildNoteLookup(notes);
  const events: ManiaLaneEvent[] = [];
  for (const note of sorted) {
    events.push({
      id: `${note.id}:p`,
      beat: note.beat,
      lane: note.lane,
      holdEndBeat: note.holdEndBeat,
      kind: 'primary',
      sourceId: note.id,
    });
    if (note.dualPartnerLane !== undefined) {
      events.push({
        id: `${note.id}:d`,
        beat: note.beat,
        lane: note.dualPartnerLane,
        holdEndBeat: note.holdEndBeat,
        kind: 'dual',
        sourceId: note.id,
      });
    }
  }
  events.sort((a, b) => {
    if (a.beat !== b.beat) {
      return a.beat - b.beat;
    }
    return a.id.localeCompare(b.id);
  });
  return events;
}

export function createManiaInitialState(_chart: ManiaChart): ManiaEngineState {
  return {
    beat: 0,
    combo: 0,
    maxCombo: 0,
    perfects: 0,
    goods: 0,
    misses: 0,
    score: 0,
    resolutions: [],
    alive: true,
    completed: false,
    deathReason: null,
    deathBeat: null,
    resolvedIds: new Set(),
    resolvedReleaseIds: new Set(),
    heldLanes: new Map(),
    hp: STARTING_HP,
    maxHp: MAX_HP,
    lastMissBeat: null,
    consecutiveMissCount: 0,
    regenProgressBeats: 0,
  };
}

/**
 * Reset the chart-relative parts of a mania engine state so the next
 * loop sees a fresh chart but keeps the player's accumulated
 * score/combo/verdict counters. Mirrors the equivalent Circles + Pulse
 * resets — they MUST agree on what carries over, or replay validation
 * drifts as soon as it re-simulates a multi-loop run.
 *
 * Mutates the passed state in place.
 */
export function resetManiaEngineStateForNextLoop(state: ManiaEngineState): void {
  state.beat = 0;
  state.resolutions = [];
  state.alive = true;
  state.completed = false;
  state.deathReason = null;
  state.deathBeat = null;
  state.resolvedIds = new Set();
  state.resolvedReleaseIds = new Set();
  state.heldLanes = new Map();
  state.hp = state.maxHp;
  state.lastMissBeat = null;
  state.consecutiveMissCount = 0;
  state.regenProgressBeats = 0;
  // score, combo, maxCombo, perfects, goods, misses are intentionally
  // preserved — they accumulate across loops so the results overlay
  // can show the run total.
}
